import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Center, Text } from "@chakra-ui/react";
import { FullPageSpinner } from "components/Spinner";
import { useSessionCtx, useSessionJWT, redirectWindowLocation } from "utils";
import getReturnRedirectData from "utils/getReturnRedirectData";
import { getAllowedRedirectPatterns } from "./allowedRedirectPatternsStorage";
import ErrorViewAndRedirect from "./ErrorViewAndRedirect";
import { exchangePlaidPublicToken } from "api/payments";
import { FLOW_STATE } from "./PlaidMain";

/**
 * @name PlaidLinkWrapper
 * @param {string} plaidLinkToken
 * @param {string} [receivedRedirectUri]
 * @returns JSX.Element
 * @description Handles interaction with the usePlaidLink hook.
 * This component is used in 2 spots.
 * 1. When a user is first shown the Plaid UI to select a bank account
 *    that they want to connect with Tilia.  For this, only the plaidLinkToken
 *    is needed.
 * 2. When a user returns from an OAuth flow at a separate banking
 *    institution which requires re-opening the Plaid UI to close the loop
 *    on linking the bank account.  For this we still need the
 *    plaidLinkToken (which should be stored in sessionStorage), and also
 *    needs the receivedRedirectUri which contains a search param 'oauth_state_id'
 *    that is used to restore the user's Plaid state.
 * The component will call the exchangePlaidPublicToken api endpoint
 * with the public token it receives from Plaid.
 * When the user is done either by exiting the UI or completing the
 * account connection, this component handles redirecting the user back to
 * where they started from.  This is done by reading information stored in
 * the sessionCtx state (also sessionStorage).
 * If the user has successfully connected a bank account the redirect url
 * will include the ID of the new payment method. Format: plaid.complete.{PMID}
 *
 * One big thing to note, is this page is NEVER expected to run
 * within the iframe as the button that redirects to this page directs
 * the user out of the iFrame.
 */
const PlaidLinkWrapper = ({ plaidLinkToken, receivedRedirectUri = null, usePlaidLink }) => {
  const history = useHistory();
  const { sessionCtx, setFlowState } = useSessionCtx();
  const { sessionJWT } = useSessionJWT();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [redirectData, setRedirectData] = useState(null);
  const [isMissingRequiredData] = useState(
    !plaidLinkToken || !sessionJWT || !sessionCtx || !(sessionCtx?.intCtx || sessionCtx?.flow)
  );

  // add useEffect to check for any missing required data
  useEffect(() => {
    if (isMissingRequiredData) {
      setError(new Error("Missing required data."));
    }
  }, [isMissingRequiredData]);

  useEffect(() => {
    if (!redirectData) return;
    if (redirectData.useReactRedirect) {
      // need to handle react router redirect if needed
      history.push(redirectData.url);
    } else {
      redirectWindowLocation(redirectData.url);
    }
  }, [redirectData, history]);

  // Resolve the return redirect and store it. getReturnRedirectData throws
  // RedirectNotAllowedError for a disallowed origin; React error boundaries do
  // not catch errors thrown directly inside Plaid SDK callbacks, so we push it
  // through a state updater (which throws during render) to surface the
  // "Invalid redirect domain" message via the GlobalErrorBoundary.
  const resolveRedirect = useCallback((ctx, flowState) => {
    try {
      // allowed patterns were signed into the state token and cached in sessionStorage
      // by PlaidMain (SettingsProvider is not populated on this route) - see
      // ./allowedRedirectPatternsStorage
      setRedirectData(getReturnRedirectData(ctx, flowState, getAllowedRedirectPatterns()));
    } catch (err) {
      setRedirectData(() => {
        throw err;
      });
    }
  }, []);

  const plaidSuccessHandler = useCallback(
    // second argument is metadata in case needed for debugging
    async (public_token) => {
      setIsLoading(true);
      try {
        /*****************************/
        /**
         * Because of the way this page is built with the usePlaidLink hook
         * We need to pull the updated session and ctx info directly from
         * sessionStorage as when usePlaidLink is called with this function
         * it does not yet get the updated session information.
         * TODO: There's likely a better way around this.  Maybe breaking up pages
         * from /ui/plaid/main to /ui/plaid/begin and /ui/plaid/finish, then use the existing
         * /ui/plaid/success to handle returning.
        */
        const directFromSessionStorageCTX = JSON.parse(sessionStorage.getItem("tilia.ac.ctx")); // see comment above on why we're pulling this from sessionStorage
        const directFromSessionStorageJWT = JSON.parse(sessionStorage.getItem("tilia.ac.session"));
        /*****************************/
        
        const response = await exchangePlaidPublicToken(directFromSessionStorageJWT, {
          plaid_public_token: public_token,
          use_manual_address_flow: true // if user address is missing in Plaid, this will allow setting the bank account to 'pending' state, so we can trigger the address collection flow with the user
        });
        const pmid =
          response.payload?.id || response.payload?.ID || response.payload.bank_account_id; // TODO: endpoint should not be capatalizing the ID key
        if (pmid) {
          /**
           * check if bank account is in a 'PENDING' state.
           * What does 'PENDING' mean??  Well it's a very vague way to tell
           * the widget that the bank is account is missing address information.
           * When this occurs we need to mark it in the flow state, so the
           * widget can take them through the expected address flow
          */
          const isPending = response.payload?.status === "PENDING";
          const flowStatePrefix = isPending ? FLOW_STATE.PENDING : FLOW_STATE.COMPLETE;
          const bankOwnerId = response.payload?.owner_id || null; // need this for handling pending bank account
          const flowStateSuffix = isPending && bankOwnerId ? bankOwnerId : pmid;
          const flowState = `${flowStatePrefix}.${flowStateSuffix}`;
          if (!directFromSessionStorageCTX?.intCtx) {
            // we are not redirecting back to a pub page that wraps the iframe,
            // so we need to store the flow state in sessionCtx as it will not
            // be included in the query params of the redirect url
            setFlowState(flowState);
          }
          resolveRedirect(directFromSessionStorageCTX, flowState);
        } else {
          setIsLoading(false);
          setError(new Error("Missing ID for new payment method."));
        }
      } catch (err) {
        setIsLoading(false);
        setError(err);
      }
    },
    [setFlowState, resolveRedirect]
  );

  const plaidExitHandler = useCallback(
    (err, metadata) => {
      const noData = !err && Object.values(metadata || {}).every((v) => !v); // came across case where onExit is called without any real data.  Gonna treat this as an error
      if (noData) {
        setError(new Error("onExit called with no data."));
      } else if (err) {
        // logError(sessionJWT, err);
        const { error_type } = err; // we could use error_message here, but it's possibly too descriptive: "OAuth redirect URI must be configured in the developer dashboard. See https://plaid.com/docs/#oauth-redirect-uris"
        setError(new Error(error_type || "Unknown ACH exit error."));
      } else {
        // likely user-cancelled state
        const { status } = metadata;
        const flowState = `${FLOW_STATE.CANCEL}${status ? `.${status}` : ""}`;
        if (!sessionCtx?.intCtx) {
          // we are not redirecting back to a pub page that wraps the iframe,
          // so we need to store the flow state in sessionCtx as it will not
          // be included in the query params of the redirect url
          setFlowState(flowState);
        }
        resolveRedirect(sessionCtx, flowState);
      }
    },
    [setError, sessionCtx, setFlowState, resolveRedirect]
  );

  const {
    open,
    ready,
    error: usePlaidLinkErr,
  } = usePlaidLink({
    token: plaidLinkToken,
    receivedRedirectUri,
    onSuccess: plaidSuccessHandler,
    onExit: plaidExitHandler,
  });

  if (usePlaidLinkErr) {
    /**
     * Not sure this is actually needed.  I haven't been able to trigger it in any way.
     * Plaid docs say that usePlaidLink does return an 'error' param so handling
     * in case we every get one.
     */
    setError(usePlaidLinkErr);
  }

  /**
   * useEffect to handle showing Plaid UI
   */
  useEffect(() => {
    if (ready) {
      open();
    }
  }, [ready, open]);

  let content = (
    <span data-testid="plaid-link-wrapper">
      {/* No need to show anything in UI here as Plaid will surface a modal. */}
    </span>
  );

  if (error) {
    content = <ErrorViewAndRedirect error={error} />;
  }

  if (isLoading) {
    content = <FullPageSpinner />;
  }

  if (redirectData) {
    content = <Text>Redirecting, please wait...</Text>;
  }

  return (
    <Center h="100%" flexDirection="column">
      {content}
    </Center>
  );
};

export default PlaidLinkWrapper;
